import {Trans} from '@lingui/macro';
import {isString} from 'lodash';
import React, {FC, useCallback, useState} from 'react';

import {KeyboardActionsEnum} from '../../../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {
  EventHandler,
  FrontEvent,
  HandlerOf,
  RendererOf,
} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useToggle} from '../../../../../../../srcCommon/helpers/hookHelpers';
import {useTagBulkActionPermission} from '../../../../../../../srcCommon/permissions/useBulkActionPermission';
import {
  buildLegacyEntryPointInstance,
  LegacyConversationLinkDropdownEntryPoint,
  LegacyConversationLinkDropdownEntryPointInstance,
} from '../../../../../../helpers/models/legacyApplicationHelpers';
import {ViewerDropdownInterfaceEventSourcesEnum} from '../../../../../../helpers/models/tagHelpers';
import {PermissionResult} from '../../../../../../helpers/permissionHelpers';
import {useAppDispatch} from '../../../../../../helpers/reduxHelpers';
import {ConversationBaseModel, ConversationModel} from '../../../../../../models/api/conversationModel';
import {BulkActionsTrackingTypesEnum} from '../../../../../../services/tracking';
import {createAndAddTopicToConversationsAsyncAction} from '../../../../../../store/data/conversations/topicConversationsActions';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {LegacyConversationsApplicationContextInstanceHost} from '../../../../../applications/legacyContextHosts/legacyConversationsApplicationContextInstanceHost';
import {CreateConversationTagDropdownContainer} from '../../../../../common/dropdowns/tags/createConversationTagDropdownContainer';
import {AddOrEditTopicDialogContainer} from '../../header/topicsAndTags/addOrEditTopicDialogContainer';
import {AddTopicOrTagDropdownContainer} from '../../header/topicsAndTags/addTopicOrTagDropdownContainer';

/*
 * Props.
 */

interface ConversationsTagCoordinatorProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  onlyConversation: ConversationModel | undefined;
  conversations: ReadonlyArray<ConversationBaseModel>;
  inboxesParameters: InboxesParameters;
  isBulkActionRequired: boolean;
  onPopoverToggle?: HandlerOf<boolean>;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;
  interfaceEventSource: ViewerDropdownInterfaceEventSourcesEnum;
  renderButton: RendererOf<ConversationsTagButtonProps>;
}

export interface ConversationsTagButtonProps {
  hasHover: boolean;
  permissionResult: PermissionResult;
}

/*
 * Component.
 */

export const ConversationsTagCoordinator: FC<ConversationsTagCoordinatorProps> = (props) => {
  const dispatch = useAppDispatch();
  const [createTagInitialName, setCreateTagInitialName] = useState<string>();
  const [isAddTopicOpen, toggleIsAddTopicOpen] = useToggle();
  const [selectedEntryPoint, setSelectedEntryPoint] =
    useState<LegacyConversationLinkDropdownEntryPointInstance>();

  const bulkActionPermission = useTagBulkActionPermission(
    props.isBulkActionRequired,
    props.inboxesParameters,
  );

  /*
   * Event handlers.
   */

  const onCreateTagSelect = useCallback((e: FrontEvent, initialTagName: string | undefined) => {
    e.preventDefault(); // Keep popover open.
    setCreateTagInitialName(initialTagName || '');
  }, []);

  const onEntryPointSelect = (entryPoint: LegacyConversationLinkDropdownEntryPoint) => {
    if (props.isBulkActionRequired) {
      return;
    }

    setSelectedEntryPoint(buildLegacyEntryPointInstance(entryPoint));
  };

  const createAndAddTopic = (link: string, name: string) => {
    if (props.isBulkActionRequired) {
      return;
    }

    const conversationIds = props.conversations.map((conversation) => conversation.id);
    dispatch(createAndAddTopicToConversationsAsyncAction(conversationIds, link, name));
  };

  /*
   * Render.
   */

  const renderAddDropdown = (popoverProps: PopoverTransferProps) => {
    const onAddTopicSelect: EventHandler = (e) => {
      e.preventDefault(); // Keep the dropdown open.

      if (props.isBulkActionRequired) {
        return;
      }

      toggleIsAddTopicOpen(); // Switch to Add Link.
    };
    const placementValue =
      props.interfaceEventSource === ViewerDropdownInterfaceEventSourcesEnum.TOPBAR
        ? 'bottom-end'
        : 'top-end';

    return (
      <AddTopicOrTagDropdownContainer
        {...popoverProps}
        {...props}
        onCreateTagSelect={onCreateTagSelect}
        onEntryPointSelect={props.isBulkActionRequired ? undefined : onEntryPointSelect}
        onAddTopicSelect={props.isBulkActionRequired ? undefined : onAddTopicSelect}
        inboxesParameters={props.inboxesParameters}
        panelId={props.panelId}
        placement={placementValue}
        onInterfaceEvent={props.onInterfaceEvent}
        interfaceEventSource={props.interfaceEventSource}
        shouldUntether
      />
    );
  };

  const renderCreateTagDropdown = (popoverProps: PopoverTransferProps, initialTagName: string) => {
    const onRequestClose = () => {
      popoverProps.onRequestClose(); // Close the dropdown.
      setCreateTagInitialName(undefined); // Switch back to List.
    };

    const onBackButtonClick = () => {
      setCreateTagInitialName(undefined); // Switch back to List.
    };

    return (
      <CreateConversationTagDropdownContainer
        {...popoverProps}
        {...props}
        initialName={initialTagName}
        onRequestClose={onRequestClose}
        onBackButtonClick={onBackButtonClick}
        backButtonLabel={<Trans id="conversation-tags-back">Back to tags</Trans>}
        inboxesParameters={props.inboxesParameters}
        panelId={props.panelId}
        onInterfaceEvent={props.onInterfaceEvent}
        cellsContext={props.cellsContext}
        shouldUntether
        shouldSkipApplyMaxSize
      />
    );
  };

  const renderAddTopic = (popoverProps: PopoverTransferProps) => {
    const onRequestClose = () => {
      popoverProps.onRequestClose(); // Close the dropdown
      toggleIsAddTopicOpen(); // Switch back to List
    };

    return (
      <AddOrEditTopicDialogContainer
        {...props}
        createAndAddTopic={createAndAddTopic}
        onRequestClose={onRequestClose}
      />
    );
  };

  const renderPopover = (popoverProps: PopoverTransferProps) => {
    if (isString(createTagInitialName)) {
      return renderCreateTagDropdown(popoverProps, createTagInitialName);
    }

    if (isAddTopicOpen) {
      return renderAddTopic(popoverProps);
    }

    return renderAddDropdown(popoverProps);
  };

  return (
    <>
      <PopoverCoordinator
        renderButton={(isOpen) =>
          props.renderButton({
            hasHover: isOpen,
            permissionResult: bulkActionPermission,
          })
        }
        renderModal={renderPopover}
        keyboardAction={KeyboardActionsEnum.TAG}
        onPopoverToggle={props.onPopoverToggle}
      />
      {selectedEntryPoint && (
        <LegacyConversationsApplicationContextInstanceHost
          entryPointInstance={selectedEntryPoint}
          onlyConversation={props.onlyConversation}
          conversations={props.conversations}
          panelId={props.panelId}
          cellsContext={props.cellsContext}
        />
      )}
    </>
  );
};
